import { Table, TableBody, TableCell, TableContainer, TableRow } from "@mui/material"
import { useEffect } from "react"
import type { ITrainingReport } from "types/trainingReportTypes"

import icon_collision from "../../images/icon_collision.png"
import icon_improperolp from "../../images/icon_improperolp.png"
import icon_loadswing from "../../images/icon_loadswing.png"
import icon_movewithactivecontroller from "../../images/icon_moveactivecontroller.png"
import icon_moveunderboom from "../../images/icon_moveunderboom.png"
import icon_notgrounded from "../../images/icon_notgrounded.png"
import icon_userblockstabilizer from "../../images/icon_userblockstabilizer.png"

interface Props {
  trainingReport: ITrainingReport
}

export enum PerformanceSuggestion {
  UserMoveUnderBoom,
  UserMoveWithActiveController,
  UserInFrontOfStabilizer,
  LoadSwing,
  OlpWarning,
  StabilizerNotSet,
  Collision,
}

const getSuggestions = (data: ITrainingReport): Map<PerformanceSuggestion, string> => {
  const suggestions = new Map<PerformanceSuggestion, string>()

  if (
    parseFloat(
      data.parameters.find((x) => x.name === "examsafety_collision_tennisball")?.value ?? "0",
    ) > 0 ||
    parseFloat(data.parameters.find((x) => x.name === "examsafety_collision_pole")?.value ?? "0") >
      0 ||
    parseFloat(data.parameters.find((x) => x.name === "safety_collision_count")?.value ?? "0") > 0
  )
    suggestions.set(
      PerformanceSuggestion.Collision,
      "In order to avoid collisions in narrow areas, consider using micro mode for finer crane movements.",
    )

  if (
    parseFloat(
      data.parameters.find((x) => x.name === "examsafety_moved_controller_active_count")?.value ??
        "0",
    ) > 0 ||
    parseFloat(
      data.parameters.find((x) => x.name === "safety_moved_controller_active_count")?.value ?? "0",
    ) > 0
  )
    suggestions.set(
      PerformanceSuggestion.UserMoveWithActiveController,
      "Always turn off the remote controller before moving.",
    )

  if (
    parseFloat(
      data.parameters.find((x) => x.name === "examsafety_moved_under_boom")?.value ?? "0",
    ) > 0 ||
    parseFloat(
      data.parameters.find((x) => x.name === "safety_moved_under_boom_count")?.value ?? "0",
    ) > 0
  )
    suggestions.set(
      PerformanceSuggestion.UserMoveUnderBoom,
      "Moving under the crane boom can lead to dangerous situations.",
    )

  if (
    parseFloat(data.parameters.find((x) => x.name === "examtask_load_swing")?.value ?? "100") <
      80 ||
    parseFloat(data.parameters.find((x) => x.name === "safety_load_swing")?.value ?? "1") < 0.8
  )
    suggestions.set(
      PerformanceSuggestion.LoadSwing,
      "Rapid or fast lever movements can lead to the load swinging dangerously.",
    )

  if (
    (data.parameters.find((x) => x.name === "safety_stabilisers_grounded")?.value ?? "True") ===
    "False"
  )
    suggestions.set(
      PerformanceSuggestion.StabilizerNotSet,
      "To keep the crane level during operation, make sure that the stabilizers are grounded.",
    )

  if (parseFloat(data.parameters.find((x) => x.name === "olp_trigger_count")?.value ?? "0") > 0)
    suggestions.set(
      PerformanceSuggestion.OlpWarning,
      "When lifting heavy loads, pay attention to the Overload Protection System indicators.",
    )

  if (
    parseFloat(
      data.parameters.find((x) => x.name === "safety_user_block_stabilizer")?.value ?? "0",
    ) > 0
  )
    suggestions.set(
      PerformanceSuggestion.UserInFrontOfStabilizer,
      "Standing in front of active stabilizers can lead to dangerous situations.",
    )

  return suggestions
}

const iconStyle = () => {
  return { filter: "brightness(0.6)" }
}

const getSuggestionIcon = (suggestion: PerformanceSuggestion) => {
  switch (suggestion) {
    case PerformanceSuggestion.Collision:
      return <img width={150} style={iconStyle()} src={icon_collision} alt="collision" />
    case PerformanceSuggestion.LoadSwing:
      return <img width={150} style={iconStyle()} src={icon_loadswing} alt="loadswing" />
    case PerformanceSuggestion.OlpWarning:
      return <img width={150} style={iconStyle()} src={icon_improperolp} alt="olpwarning" />
    case PerformanceSuggestion.StabilizerNotSet:
      return <img width={150} style={iconStyle()} src={icon_notgrounded} alt="stabnotset" />
    case PerformanceSuggestion.UserInFrontOfStabilizer:
      return (
        <img
          width={150}
          style={iconStyle()}
          src={icon_userblockstabilizer}
          alt="unserinfrontofstab"
        />
      )
    case PerformanceSuggestion.UserMoveUnderBoom:
      return (
        <img width={150} style={iconStyle()} src={icon_moveunderboom} alt="usermoveunderboom" />
      )
    case PerformanceSuggestion.UserMoveWithActiveController:
      return (
        <img
          width={150}
          style={iconStyle()}
          src={icon_movewithactivecontroller}
          alt="usermovewithactivecontroller"
        />
      )
  }
}

export function TrainingReportSuggestionList({ trainingReport }: Props) {
  const suggestions = getSuggestions(trainingReport)

  useEffect(() => {}, [])

  return (
    <TableContainer>
      <Table size="medium">
        <TableBody>
          {suggestions.size > 0 ? (
            Array.from(suggestions).map((parameter) => (
              <TableRow
                key={parameter[1]}
                sx={{
                  "&:last-child td, &:last-child th": { border: 0 },
                }}
              >
                <TableCell align="left">{getSuggestionIcon(parameter[0])}</TableCell>
                <TableCell align="left">{parameter[1]}</TableCell>
              </TableRow>
            ))
          ) : (
            <TableRow>
              <TableCell>-</TableCell>
            </TableRow>
          )}
        </TableBody>
      </Table>
    </TableContainer>
  )
}
